import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnProps } from 'antd/lib/table';
import { Card, List, Typography, Table } from 'antd';
import { Link } from 'react-router-dom';
import { useAppointmentDetails } from 'providers/AppointmentDetails';
import {
  AppointmentFileRow,
  fetchPreviousFilesByPatient
} from 'services/api/appointments';

import { parseJson } from 'utils/json';
import { validateLink } from 'utils/link';
import { getIdStart } from 'utils/string';
import DownloadFile from './DownloadFile';

const { Title, Link: TextLink } = Typography;

type PreviousFileRow = {
  id: number;
  appointment_id: string;
  created_at?: string;
  file: string;
};

const Files: FC = () => {
  // translations
  const { t } = useTranslation();
  const { appointment } = useAppointmentDetails();
  const patientId = appointment?.patient?.id;

  const [previousFiles, setPreviousFiles] = useState<PreviousFileRow[]>([]);

  useEffect(() => {
    if (!patientId) return;

    fetchPreviousFilesByPatient(patientId)
      .then((rows: AppointmentFileRow[]) =>
        setPreviousFiles(
          rows.map(r => ({
            id: r.id,
            appointment_id: String(r.appointmentId),
            created_at: r.createdAt,
            file: r.file
          }))
        )
      )
      .catch(() => setPreviousFiles([]));
  }, [patientId]);

  const columns: ColumnProps<PreviousFileRow>[] = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'appointment_id',
        key: 'appointment_id',
        render: (id: string) => (
          <Link to={`/appointments/details/${id}`}>{getIdStart(id)}</Link>
        )
      },
      {
        title: t('appointments_details.creation_date'),
        dataIndex: 'created_at',
        key: 'created_at'
      },
      {
        title: t('appointments_details.files'),
        dataIndex: 'file',
        key: 'file',
        render: (value: string) => {
          return <DownloadFile value={value} patientId={patientId} />;
        }
      }
    ],
    [patientId, t]
  );

  return (
    <>
      <Card
        bordered={false}
        bodyStyle={{
          padding: '24px 0 0 40px',
          marginBottom: 32,
          boxShadow: '0px 4px 20px #E7EAF6'
        }}
      >
        <Title level={5} style={{ marginBottom: 16 }}>
          {t('appointments_details.files_documents')}
        </Title>
        <List
          itemLayout="horizontal"
          dataSource={(appointment?.files || []) as AppointmentFileRow[]}
          style={{
            maxHeight: 200,
            overflowY: 'auto'
          }}
          renderItem={item => {
            return (
              <List.Item key={item.id}>
                <List.Item.Meta
                  title={
                    <DownloadFile value={item.file} patientId={patientId} />
                  }
                />
              </List.Item>
            );
          }}
        />
      </Card>
      <Card
        bordered={false}
        bodyStyle={{
          padding: '24px 0 0 40px',
          marginBottom: 32,
          boxShadow: '0px 4px 20px #E7EAF6'
        }}
      >
        <Title level={5} style={{ marginBottom: 16 }}>
          {t('appointments_details.attached_links')}
        </Title>
        <List
          itemLayout="horizontal"
          dataSource={parseJson(appointment?.links || [])}
          style={{
            maxHeight: 200,
            overflowY: 'auto'
          }}
          renderItem={(item: string, index: number) => (
            <List.Item key={`link-item-${index}`}>
              <List.Item.Meta
                title={
                  <a href={validateLink(item)} target="_blank" rel="noreferrer">
                    <TextLink style={{ fontWeight: 500, marginBottom: 0 }}>
                      {item}
                    </TextLink>
                  </a>
                }
              />
            </List.Item>
          )}
        />
      </Card>
      <Title level={5} style={{ paddingLeft: 8, marginBottom: 24 }}>
        {t('appointments_details.previous_appointments_files')}
      </Title>
      <Table dataSource={previousFiles} columns={columns} rowKey="id" />
    </>
  );
};

export default Files;
